import { splitOnUuids } from '@orn/global/ids/uuid.js';
import { splitOnMatches, type ITextSegment } from '@orn/shared-web/utils/splitOnMatches';

/** One stretch of rendered text: either a whole id, or the plain text between ids. */
export interface IInspectLinkedRun {
  /** The id this run links to the object inspector, or null for the text between ids. */
  id: string | null;
  /** The run's text cut into search hits and misses; concatenated, they are the run's text. */
  segments: ITextSegment[];
}

/**
 * Cuts `text` at every id it holds, each run carrying its share of the search
 * hits. Hits are found across the whole string *before* it is cut, so one that
 * straddles an id's edge still marks both sides of the link — finding them per
 * run would silently drop it, and the per-message hit badge counts it either
 * way.
 *
 * Returns null when the text holds no id, so the caller can fall back to the
 * cheaper whole-string renderer instead of rebuilding one run.
 */
export function splitIntoInspectLinkedRuns(text: string, query: string | null | undefined): IInspectLinkedRun[] | null {
  const pieces = splitOnUuids(text);
  if (!pieces.some((piece) => piece.isUuid)) {
    return null;
  }
  const hits = splitOnMatches(text, query ?? '');
  let start = 0;
  return pieces.map((piece) => {
    const end = start + piece.text.length;
    const run: IInspectLinkedRun = { id: piece.isUuid ? piece.text : null, segments: sliceSegments(hits, start, end) };
    start = end;
    return run;
  });
}

/** The part of `segments` between the two offsets, cut at both edges. */
function sliceSegments(segments: ITextSegment[], start: number, end: number): ITextSegment[] {
  const sliced: ITextSegment[] = [];
  let segmentStart = 0;
  for (const segment of segments) {
    const segmentEnd = segmentStart + segment.text.length;
    if (segmentEnd > start && segmentStart < end) {
      sliced.push({
        text: segment.text.slice(Math.max(start - segmentStart, 0), Math.min(end, segmentEnd) - segmentStart),
        isMatch: segment.isMatch,
      });
    }
    segmentStart = segmentEnd;
  }
  return sliced;
}
